import React from "react";

import { Col, Form, Row } from "react-bootstrap";

import classNames from "classnames";
import { Control, Controller, FieldErrors } from "react-hook-form";

import { ParticipantTypes } from "@api/graphql";
import { FormErrorText } from "@components/form/FormErrorText";
import { mandatoryFields } from "@pages/register/hooks/errorResolver";
import { useIsInternalUserCreatingAccount } from "@pages/register/hooks/useIsInternalUserCreatingAccount";
import { UserDetailsInput } from "@pages/register/steps/RegisterFormTypes";

import styles from "./index.module.scss";

interface FinanceContactInformationFormProps {
  control: Control<UserDetailsInput>;
  errors: FieldErrors<UserDetailsInput>;
  accountStatus?: string;
  participantTypes?: ParticipantTypes[];
}

export const FinanceContactInformationForm: React.FC<FinanceContactInformationFormProps> = ({
  control,
  errors,
  accountStatus,
  participantTypes,
}) => {
  const isInternalUserCreatingAccount = useIsInternalUserCreatingAccount();

  // Dynamic mandatory fields for AMPs/Art Centres/CMOs (only when both props are provided)
  const isAmpCmoArtCentre = participantTypes?.some((type) =>
    [ParticipantTypes.AMP, ParticipantTypes.CMO, ParticipantTypes.ART_CENTRE].includes(type),
  );
  const isDynamicFieldMandatory =
    accountStatus &&
    participantTypes &&
    isAmpCmoArtCentre &&
    (accountStatus === "ACTIVE" || accountStatus === "PENDING_APPROVAL");

  const isFieldMandatory = (field: keyof typeof mandatoryFields.financeContact): boolean => {
    // Use dynamic validation if applicable, otherwise use original logic
    if (accountStatus && participantTypes && isAmpCmoArtCentre) {
      return isDynamicFieldMandatory || false;
    }
    return mandatoryFields.financeContact[field]({ isInternalUserCreatingAccount }) || false;
  };

  const renderLabel = (label: string, isMandatory: boolean) => (isMandatory ? `${label} *` : label);
  return (
    <div className={styles["form-section"]}>
      <div className={classNames(styles["form-help-information"], "b5 medium")}>
        Please provide details for your finance contact. This person will receive resale royalty invoices and payment
        queries directly.
      </div>

      <Row>
        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label>{renderLabel("Preferred pronoun", false)}</Form.Label>
            <Controller
              name="finance_contact.pronouns"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="text"
                  placeholder="Enter your preferred pronoun"
                  isInvalid={!!errors?.finance_contact?.pronouns}
                />
              )}
            />
            <FormErrorText error={errors?.finance_contact?.pronouns} />
          </Form.Group>
        </Col>

        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label>{renderLabel("First Name", isFieldMandatory("firstName"))}</Form.Label>
            <Controller
              name="finance_contact.first_name"
              control={control}
              defaultValue=""
              render={({ field }) => <Form.Control {...field} type="text" placeholder="Enter first name" />}
            />
            <FormErrorText error={errors?.finance_contact?.first_name} />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label>{renderLabel("Last Name", isFieldMandatory("lastName"))}</Form.Label>
            <Controller
              name="finance_contact.last_name"
              control={control}
              defaultValue=""
              render={({ field }) => <Form.Control {...field} type="text" placeholder="Enter last name" />}
            />
            <FormErrorText error={errors?.finance_contact?.last_name} />
          </Form.Group>
        </Col>

        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label>{renderLabel("Email address", isFieldMandatory("email"))}</Form.Label>
            <Controller
              name="finance_contact.finance_email"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="email"
                  placeholder="Enter your email address"
                  isInvalid={!!errors?.finance_contact?.finance_email}
                />
              )}
            />
            <FormErrorText error={errors?.finance_contact?.finance_email} />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label>{renderLabel("Mobile number", isFieldMandatory("phoneNumber"))}</Form.Label>
            <Controller
              name="finance_contact.finance_phone"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="tel"
                  placeholder="Enter mobile number"
                  isInvalid={!!errors?.finance_contact?.finance_phone}
                />
              )}
            />
            <FormErrorText error={errors?.finance_contact?.finance_phone} />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};
